import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import { PATTERN } from '../../../render/patterns/patternIds';
import { SURFACE } from '../../mesh/attributes';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { boxSolid } from '../solids';
import { buildConventRange } from './conventRange';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf, planBounds, planFrameOf } from './planFrame';
import { NO_TRACERY } from './tracery/tracerySink';

/** A broad early house, not the ornate 1568 front or the later paired wings. */
export function eersteWeeshuisPlan(landmark: Landmark) {
  const plan = planFrameOf(landmark.footprint, landmark.heading),
    b = planBounds(plan);
  return {
    plan,
    part: {
      length: b.u1 - b.u0,
      width: b.v1 - b.v0,
      eaves: Number(landmark.params['height']),
      ridge: Number(landmark.params['ridgeHeight']),
      floors: 2,
      bays: 4,
      passage: 0,
      doorSide: 2,
      windowRows: [
        { sill: 1.05, top: 2.7, width: 0.95 },
        { sill: 4.15, top: 5.8, width: 0.95 },
      ],
    },
  };
}

/** Materials, openings and chimney are fitted period estimates; no later ornament is imported. */
export function buildEersteWeeshuis(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & { readonly tile: LinearRGB },
  ids: { readonly edgeId: number; readonly seed: number }
): void {
  const { plan, part } = eersteWeeshuisPlan(landmark),
    frame = meshFrameOf(plan),
    masonry = paintedChapelStyles(colors, ids, NO_TRACERY),
    styles = {
      ...masonry,
      wall: { ...masonry.wall, surface: SURFACE.brick },
      roof: {
        ...masonry.roof,
        color: colors.tile,
        surface: SURFACE.roofTile,
        pattern: PATTERN.roofTileHollePan,
      },
    };
  buildConventRange(builder, frame, styles, part);
  const u = -part.length * 0.28;
  boxSolid(
    builder,
    styles.wall,
    frame,
    [u - 0.35, part.ridge - 0.3, -0.3],
    [u + 0.35, part.ridge + 1.1, 0.3],
    { top: true, bottom: true }
  );
}
